import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Add01Icon,
  AiMagicIcon,
  ArrowLeft01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  BotIcon,
  BookOpen01Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  Copy01Icon,
  CpuIcon,
  Download01Icon,
  GlobeIcon,
  Edit02Icon,
  EyeIcon,
  Search01Icon,
  ServerStack01Icon,
  Shield01Icon,
  StarIcon,
  Tick01Icon,
  WorkflowCircle06Icon,
} from '@hugeicons/core-free-icons';
import { api, apiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Registries from '@/pages/Registries';
import AiContextManager from '@/pages/toolkit/AiContextManager';
import { useAuth } from '@/context/AuthContext';

const TYPES = {
  prompts: {
    singular: 'prompt',
    label: 'Prompts',
    icon: AiMagicIcon,
    description: 'Instrucciones probadas para obtener resultados consistentes y seguros.',
  },
  skills: {
    singular: 'skill',
    label: 'Skills',
    icon: WorkflowCircle06Icon,
    description: 'Procedimientos reutilizables para resolver tareas de principio a fin.',
  },
  steerings: {
    singular: 'steering',
    label: 'Steerings',
    icon: Shield01Icon,
    description: 'Reglas que orientan el comportamiento seguro de agentes y soluciones.',
  },
};
const TECHNICAL_TYPES = {
  modelos: {
    singular: 'modelo',
    label: 'Modelos',
    icon: CpuIcon,
    description: 'Modelos de IA evaluados y compartidos con capacidades, límites y usos recomendados.',
  },
  mcp: {
    singular: 'servidor MCP',
    label: 'MCP',
    icon: ServerStack01Icon,
    description: 'Servidores MCP reutilizables para conectar agentes con herramientas y fuentes autorizadas.',
  },
  agentes: {
    singular: 'agente',
    label: 'Agentes',
    icon: BotIcon,
    description: 'Agentes institucionales documentados con propósito, responsables, controles y dependencias.',
  },
};
const RESOURCE_TYPES = { ...TYPES, ...TECHNICAL_TYPES };
const TYPE_BY_DB_NAME = {
  prompt: TYPES.prompts,
  skill: TYPES.skills,
  steering: TYPES.steerings,
  model: TECHNICAL_TYPES.modelos,
  mcp: TECHNICAL_TYPES.mcp,
  agent: TECHNICAL_TYPES.agentes,
};
const STATUS = {
  approved: ['Publicado', 'bg-muted/50 border-border text-foreground', 'bg-emerald-500'],
  review: ['En revisión', 'bg-muted/50 border-border text-foreground', 'bg-blue-500'],
  draft: ['Borrador', 'bg-muted/50 border-border text-foreground', 'bg-amber-500'],
  rejected: ['Requiere ajustes', 'bg-muted/50 border-border text-foreground', 'bg-rose-500'],
};

export default function ToolkitHub() {
  const { type = 'prompts', assetId } = useParams();
  const location = useLocation();
  const { can } = useAuth();
  const isQueue = location.pathname.endsWith('/review-queue');
  const meta = RESOURCE_TYPES[type] || TYPES.prompts;
  return (
    <div className="min-h-[70vh]">
      <ToolkitHero />
      <ToolkitNav current={isQueue ? 'queue' : type} isAdmin={can?.admin} />
      <div className="mx-auto max-w-7xl px-6 py-10">
        {isQueue ? (
          <ReviewQueue />
        ) : type === 'contexto' ? (
          can?.admin ? (
            <AiContextManager />
          ) : (
            <p className="text-sm text-muted-foreground" role="alert">
              No tienes permisos para ver esta sección.
            </p>
          )
        ) : type === 'ecosistema' ? (
          <Registries embedded initialView="ecosystem" ecosystemOnly />
        ) : assetId ? (
          <AssetDetail assetId={assetId} type={type} />
        ) : (
          <AssetLibrary type={type} meta={meta} />
        )}
      </div>
    </div>
  );
}

function ToolkitHero() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api
      .get('/toolkit/summary')
      .then(({ data }) => setSummary(data))
      .catch(() => {});
  }, []);
  const published = summary?.types
    ? Object.values(summary.types).reduce((sum, item) => sum + Number(item.approved || 0), 0)
    : ['prompts', 'skills', 'steerings', 'models', 'mcp', 'agents'].reduce(
        (sum, key) => sum + Number(summary?.[key] || 0),
        0,
      );
  const uses = Object.values(summary?.types || {}).reduce((sum, item) => sum + Number(item.uses || 0), 0);
  return (
    <section className="relative overflow-hidden border-b bg-gradient-to-br from-primary/[0.09] via-background to-secondary/[0.09]">
      <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
      <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 md:grid-cols-[1fr_auto] md:items-end">
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-4 border-primary/25 bg-background/70 text-primary">
            Toolkit colaborativo VIA
          </Badge>
          <h1 className="font-display text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            Recursos que convierten conocimiento en resultados
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground">
            Encuentra, reutiliza y comparte recursos institucionales para trabajar con IA de forma más rápida,
            consistente y segura.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <Metric value={published || '—'} label="publicados" />
          <Metric value={uses || '—'} label="usos" />
          <Metric value={summary?.pending ?? summary?.pending_review ?? '—'} label="por revisar" />
        </div>
      </div>
    </section>
  );
}

function Metric({ value, label }) {
  return (
    <div className="min-w-24 rounded-2xl border bg-card/80 px-4 py-3 backdrop-blur">
      <p className="font-display text-2xl font-bold text-primary">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function ToolkitNav({ current, isAdmin }) {
  const items = [
    ...Object.entries(TYPES).map(([key, value]) => ({
      key,
      to: `/toolkit/${key}`,
      label: value.label,
      icon: value.icon,
    })),
    ...Object.entries(TECHNICAL_TYPES).map(([key, value]) => ({
      key,
      to: `/toolkit/${key}`,
      label: value.label,
      icon: value.icon,
    })),
    { key: 'ecosistema', to: '/toolkit/ecosistema', label: 'Ecosistema', icon: GlobeIcon },
    { key: 'queue', to: '/toolkit/review-queue', label: 'Cola de revisión', icon: Clock01Icon },
    ...(isAdmin ? [{ key: 'contexto', to: '/toolkit/contexto', label: 'Contexto IA', icon: BookOpen01Icon }] : []),
  ];
  return (
    <div className="sticky top-14 z-30 border-b bg-background/90 backdrop-blur">
      <nav className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-6" aria-label="Secciones del Toolkit">
        {items.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            className={cn(
              'flex shrink-0 items-center gap-2 border-b-2 px-4 py-4 text-sm font-semibold transition-colors',
              current === item.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}>
            <HugeiconsIcon icon={item.icon} size={18} />
            {item.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}

function AssetLibrary({ type, meta }) {
  const [data, setData] = useState({ items: [], domains: [], tags: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [domain, setDomain] = useState('all');
  const [tag, setTag] = useState('all');
  const [order, setOrder] = useState('recent');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get(`/toolkit/${type}`, {
        params: {
          q: query || undefined,
          domain: domain === 'all' ? undefined : domain,
          tag: tag === 'all' ? undefined : tag,
          order,
          page,
        },
      });
      setData(result);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setLoading(false);
    }
  }, [type, query, domain, tag, order, page]);
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);
  useEffect(() => {
    setPage(1);
  }, [type, query, domain, tag, order]);
  return (
    <>
      <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
        <div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2 text-primary">
              <HugeiconsIcon icon={meta.icon} size={24} />
            </div>
            <h2 className="font-display text-3xl font-bold">{meta.label}</h2>
          </div>
          <p className="mt-3 text-muted-foreground">{meta.description}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <HugeiconsIcon icon={Add01Icon} size={18} />
          Compartir {meta.singular}
        </Button>
      </div>
      <div className="grid items-start gap-7 lg:grid-cols-[250px_minmax(0,1fr)]">
        <CatalogSidebar
          meta={meta}
          data={data}
          query={query}
          setQuery={setQuery}
          domain={domain}
          setDomain={setDomain}
          tag={tag}
          setTag={setTag}
          order={order}
          setOrder={setOrder}
          onClear={() => {
            setQuery('');
            setDomain('all');
            setTag('all');
            setOrder('recent');
          }}
        />
        <div className="min-w-0">
          {loading ? (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-64 rounded-2xl" />
              ))}
            </div>
          ) : data.items.length ? (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {data.items.map((asset) => (
                <AssetCard key={asset.id} asset={asset} type={type} />
              ))}
            </div>
          ) : (
            <EmptyState
              title="No encontramos recursos"
              body="Prueba otros filtros o comparte el primer recurso de esta categoría."
              action={() => setCreateOpen(true)}
            />
          )}
          {data.total > 20 && (
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">Página {page}</span>
              <Button variant="outline" disabled={page * 20 >= data.total} onClick={() => setPage((p) => p + 1)}>
                Siguiente
              </Button>
            </div>
          )}
        </div>
      </div>
      <AssetEditor open={createOpen} onOpenChange={setCreateOpen} type={type} submitOnSave onSaved={load} />
    </>
  );
}

function CatalogSidebar({ meta, data, query, setQuery, domain, setDomain, tag, setTag, order, setOrder, onClear }) {
  const totalUses = data.items.reduce((sum, asset) => sum + Number(asset.usage_count || 0), 0);
  const recent = data.items.slice(0, 3);
  const activeFilters =
    Number(Boolean(query)) + Number(domain !== 'all') + Number(tag !== 'all') + Number(order !== 'recent');
  return (
    <aside className="flex flex-col gap-4 lg:sticky lg:top-32">
      <Card className="overflow-hidden border-border/80 shadow-none">
        <CardHeader className="border-b bg-gradient-to-br from-primary/10 via-background to-secondary/10 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm">Filtros del catálogo</CardTitle>
              <p className="mt-1 text-[11px] text-muted-foreground">Refina los recursos disponibles</p>
            </div>
            {activeFilters > 0 && <Badge className="rounded-full">{activeFilters}</Badge>}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 pt-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`search-${meta.label}`} className="text-xs text-muted-foreground">
              Buscar
            </Label>
            <div className="relative">
              <HugeiconsIcon
                icon={Search01Icon}
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                id={`search-${meta.label}`}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Buscar ${meta.label.toLowerCase()}…`}
                className="pl-9"
              />
            </div>
          </div>
          <SearchableSelect
            label="Dominio"
            value={domain}
            onChange={setDomain}
            allLabel="Todos los dominios"
            options={data.domains}
            searchPlaceholder="Buscar dominio…"
          />
          <SearchableSelect
            label="Etiqueta"
            value={tag}
            onChange={setTag}
            allLabel="Todas las etiquetas"
            options={data.tags || []}
            searchPlaceholder="Buscar etiqueta…"
          />
          <SearchableSelect
            label="Ordenar por"
            value={order}
            onChange={setOrder}
            options={[
              { value: 'recent', label: 'Más recientes' },
              { value: 'popular', label: 'Más utilizados' },
              { value: 'rating', label: 'Mejor valorados' },
              { value: 'title', label: 'Nombre A–Z' },
            ]}
            searchPlaceholder="Buscar criterio…"
          />
          <Button variant="outline" onClick={onClear} disabled={activeFilters === 0}>
            Limpiar filtros
          </Button>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/80 shadow-none">
        <CardHeader className="border-b bg-muted/30 pb-3">
          <CardTitle className="text-sm">Resumen del catálogo</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 pt-4 text-xs">
          <SummaryRow label="Recursos publicados" value={data.total} />
          <SummaryRow label="Resultados visibles" value={data.items.length} />
          <SummaryRow label="Dominios" value={data.domains.length} />
          <SummaryRow label="Usos visibles" value={totalUses} />
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/80 shadow-none">
        <CardHeader className="border-b bg-muted/30 pb-3">
          <CardTitle className="text-sm">Agregados recientemente</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col pt-2">
          {recent.length ? (
            recent.map((asset, index) => (
              <div key={asset.id}>
                {index > 0 && <Separator />}
                <Link
                  to={`/toolkit/${Object.keys(RESOURCE_TYPES).find((key) => RESOURCE_TYPES[key] === meta)}/${asset.id}`}
                  className="group flex items-start gap-3 py-3">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <HugeiconsIcon icon={meta.icon} size={15} />
                  </span>
                  <span className="min-w-0">
                    <span className="line-clamp-2 text-xs font-semibold group-hover:text-primary">{asset.title}</span>
                    <span className="mt-1 block text-[11px] text-muted-foreground">{asset.domain}</span>
                  </span>
                </Link>
              </div>
            ))
          ) : (
            <p className="py-3 text-xs text-muted-foreground">Aún no hay recursos publicados.</p>
          )}
        </CardContent>
      </Card>
    </aside>
  );
}

function SearchableSelect({ label, value, onChange, allLabel, options, searchPlaceholder }) {
  const [open, setOpen] = useState(false);
  const normalizedOptions = options.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option,
  );
  const allOption = allLabel ? { value: 'all', label: allLabel } : null;
  const choices = allOption ? [allOption, ...normalizedOptions] : normalizedOptions;
  const selected = choices.find((option) => option.value === value);
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between px-3 font-normal">
            <span className="truncate">{selected?.label || 'Seleccionar…'}</span>
            <HugeiconsIcon icon={ArrowDown01Icon} size={15} className="shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder={searchPlaceholder || `Buscar ${label.toLowerCase()}…`} />
            <CommandList>
              <CommandEmpty>No se encontraron opciones.</CommandEmpty>
              <CommandGroup>
                {choices.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.value}`}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}>
                    <HugeiconsIcon
                      icon={Tick01Icon}
                      className={cn('text-primary', value === option.value ? 'opacity-100' : 'opacity-0')}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-muted/50">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

function AssetCard({ asset, type }) {
  const [label, classes, dotColor] = STATUS[asset.status] || STATUS.draft;
  return (
    <Card className="group flex h-full flex-col overflow-hidden border-border/80 shadow-none transition-colors duration-200 hover:border-primary/30 hover:bg-muted/10">
      <CardHeader className="pb-3">
        <div className="mb-3 flex items-center justify-between">
          <Badge variant="outline" className={cn('flex items-center gap-1.5', classes)}>
            <span className={cn('inline-block w-1.5 h-1.5 rounded-full', dotColor)} />
            {label}
          </Badge>
          <span className="text-xs font-medium text-muted-foreground">v{asset.version}</span>
        </div>
        <CardTitle className="font-display text-xl leading-snug group-hover:text-primary-glow transition-colors">
          {asset.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <p className="line-clamp-3 text-sm leading-6 text-muted-foreground">{asset.description}</p>
        <div className="mt-4 mb-5 flex flex-wrap gap-1.5">
          {asset.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="font-normal">
              {tag}
            </Badge>
          ))}
        </div>
        <div className="mt-auto flex items-end justify-between border-t pt-4">
          <div>
            <p className="text-xs font-semibold">{asset.domain}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {asset.owner_name} · {asset.usage_count} usos
            </p>
          </div>
          <Button
            asChild
            size="icon"
            variant="outline"
            className="rounded-full group-hover:border-primary group-hover:text-primary">
            <Link to={`/toolkit/${type}/${asset.id}`} aria-label={`Ver ${asset.title}`}>
              <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AssetDetail({ assetId, type }) {
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'title' | 'description' | null
  const load = useCallback(
    () =>
      api
        .get(`/toolkit/assets/${assetId}`)
        .then(({ data }) => {
          setAsset(data);
          setEditContent(data.content || '');
          setEditTitle(data.title || '');
          setEditDescription(data.description || '');
          setEditTags(data.tags || []);
        })
        .catch((e) => toast.error(apiError(e)))
        .finally(() => setLoading(false)),
    [assetId],
  );
  useEffect(() => {
    load();
  }, [load]);

  const save = async (patch) => {
    try {
      await api.patch(`/toolkit/assets/${asset.id}`, patch);
      toast.success('Actualizado');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(asset.content);
    await api.post(`/toolkit/assets/${asset.id}/use`);
    toast.success('Contenido copiado');
    load();
  };
  const download = async () => {
    const blob = new Blob([asset.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${asset.title.toLowerCase().replace(/[^a-z0-9áéíóúñ]+/gi, '-')}.md`;
    a.click();
    URL.revokeObjectURL(url);
    await api.post(`/toolkit/assets/${asset.id}/use`);
    toast.success('Recurso descargado');
    load();
  };
  const submit = async () => {
    try {
      await api.post(`/toolkit/assets/${asset.id}/submit`);
      toast.success('Enviado a la Cola de Revisión');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const rate = async (stars) => {
    try {
      await api.post(`/toolkit/assets/${asset.id}/rate`, { stars });
      toast.success('Gracias por calificar');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const addTag = () => {
    const tag = newTag
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9áéíóúñ-]/g, '');
    if (tag && !editTags.includes(tag)) {
      const updated = [...editTags, tag];
      setEditTags(updated);
      save({ tags: updated });
    }
    setNewTag('');
  };
  const removeTag = (tag) => {
    const updated = editTags.filter((t) => t !== tag);
    setEditTags(updated);
    save({ tags: updated });
  };

  if (loading) return <Skeleton className="h-[32rem] rounded-2xl" />;
  if (!asset) return <EmptyState title="Recurso no disponible" body="Puede que todavía no esté publicado." />;
  const [label, classes, dotColor] = STATUS[asset.status] || STATUS.draft;
  const canEdit = asset.is_owner;

  return (
    <div>
      <Button variant="ghost" className="mb-4 gap-2" onClick={() => navigate(`/toolkit/${type}`)}>
        <HugeiconsIcon icon={ArrowLeft01Icon} size={18} />
        Volver a la biblioteca
      </Button>

      {/* Header */}
      <div className="rounded-2xl border border-border/50 bg-card p-6 mb-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <Badge variant="outline" className={cn('flex items-center gap-1.5 text-[11px]', classes)}>
            <span className={cn('inline-block w-1.5 h-1.5 rounded-full', dotColor)} />
            {label}
          </Badge>
          <span>·</span>
          <span>{asset.domain}</span>
          <span className="ml-auto flex items-center gap-1.5 text-[11px] font-medium text-foreground bg-muted/50 border border-border rounded-full px-2.5 py-0.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500" />v{asset.version}
          </span>
        </div>

        {/* Title — inline editable on double-click */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {editingField === 'title' ? (
                <Input
                  autoFocus
                  className="font-display text-xl font-bold h-auto py-0.5 px-1 -ml-1"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={() => {
                    save({ title: editTitle });
                    setEditingField(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      save({ title: editTitle });
                      setEditingField(null);
                    }
                    if (e.key === 'Escape') {
                      setEditTitle(asset.title);
                      setEditingField(null);
                    }
                  }}
                />
              ) : (
                <h1
                  className={cn(
                    'font-display text-xl font-bold text-foreground',
                    canEdit && 'cursor-text hover:bg-muted/30 rounded px-1 -ml-1 transition-colors',
                  )}
                  onDoubleClick={() => canEdit && setEditingField('title')}>
                  {asset.title}
                </h1>
              )}
            </div>

            {/* Description — inline editable */}
            {editingField === 'description' ? (
              <Input
                autoFocus
                className="text-sm mt-1 h-auto py-0.5 px-1 -ml-1"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                onBlur={() => {
                  save({ description: editDescription });
                  setEditingField(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    save({ description: editDescription });
                    setEditingField(null);
                  }
                  if (e.key === 'Escape') {
                    setEditDescription(asset.description);
                    setEditingField(null);
                  }
                }}
              />
            ) : (
              <p
                className={cn(
                  'text-sm text-muted-foreground mt-1',
                  canEdit && 'cursor-text hover:bg-muted/30 rounded px-1 -ml-1 transition-colors',
                )}
                onDoubleClick={() => canEdit && setEditingField('description')}>
                {asset.description || 'Sin descripción'}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={copy}>
              <HugeiconsIcon icon={Copy01Icon} size={14} className="text-blue-500" /> Copiar
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={download}>
              <HugeiconsIcon icon={Download01Icon} size={14} className="text-emerald-500" /> Descargar .md
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setEditing(!editing)}>
                <HugeiconsIcon icon={Edit02Icon} size={14} className="text-primary-glow" />{' '}
                {editing ? 'Cerrar editor' : 'Editar'}
              </Button>
            )}
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
          <span>{asset.usage_count} usos</span>
          {asset.rating && <span>{asset.rating} / 5</span>}
          <span>{asset.created_at?.slice(0, 10)}</span>
          {asset.status === 'approved' && (
            <div className="flex items-center gap-1 ml-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => rate(star)}
                  className={cn(
                    'transition-colors hover:text-amber-500',
                    star <= asset.my_rating ? 'text-amber-500' : 'text-muted-foreground/40',
                  )}
                  aria-label={`${star} estrellas`}>
                  <HugeiconsIcon icon={StarIcon} size={14} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tags — editable inline */}
        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          {editTags.map((tag) => (
            <span
              key={tag}
              className="group flex items-center gap-1 text-[11px] text-muted-foreground bg-muted/50 border border-border/50 rounded-full px-2.5 py-0.5">
              #{tag}
              {canEdit && (
                <button
                  onClick={() => removeTag(tag)}
                  className="hidden group-hover:inline text-muted-foreground/60 hover:text-destructive transition-colors"
                  aria-label={`Eliminar tag ${tag}`}>
                  &times;
                </button>
              )}
            </span>
          ))}
          {canEdit && (
            <input
              className="text-[11px] bg-transparent border-none outline-none w-20 text-muted-foreground placeholder:text-muted-foreground/40"
              placeholder="+ tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={() => {
                if (newTag.trim()) addTag();
              }}
            />
          )}
        </div>
      </div>

      {/* Content — markdown viewer or editor */}
      <div className="rounded-2xl border border-border/50 bg-card overflow-hidden">
        {editing ? (
          <div className="p-5 space-y-4">
            {/* Editor/Preview toggle */}
            <div className="flex items-center gap-2 border-b border-border/50 pb-3">
              <button
                className={cn(
                  'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors',
                  !showPreview ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
                onClick={() => setShowPreview(false)}>
                <HugeiconsIcon icon={Edit02Icon} size={13} /> Editar
              </button>
              <button
                className={cn(
                  'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors',
                  showPreview ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
                onClick={() => setShowPreview(true)}>
                <HugeiconsIcon icon={EyeIcon} size={13} /> Previsualizar
              </button>
            </div>

            {showPreview ? (
              <article className="prose-compact font-mono text-foreground min-h-[300px] p-4 rounded-lg border border-border/50 bg-muted/20">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{editContent}</ReactMarkdown>
              </article>
            ) : (
              <Textarea
                className="min-h-[400px] font-mono text-sm bg-muted/30 border-border"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Contenido en formato markdown..."
              />
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                className="gap-1.5"
                onClick={async () => {
                  try {
                    await api.patch(`/toolkit/assets/${asset.id}`, { content: editContent });
                    toast.success('Contenido guardado');
                    setEditing(false);
                    load();
                  } catch (e) {
                    toast.error(apiError(e));
                  }
                }}>
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} /> Guardar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => {
                  setEditing(false);
                  setEditContent(asset.content);
                  setShowPreview(false);
                }}>
                <HugeiconsIcon icon={CancelCircleIcon} size={14} /> Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <article className="prose-compact font-mono text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{asset.content}</ReactMarkdown>
            </article>
          </div>
        )}
      </div>

      {/* Review notes */}
      {asset.review_notes && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 mt-5 text-sm">
          <p className="font-semibold text-foreground">Observaciones del revisor</p>
          <p className="mt-1 text-muted-foreground">{asset.review_notes}</p>
        </div>
      )}

      {/* Submit to review */}
      {canEdit && ['draft', 'rejected'].includes(asset.status) && (
        <Button className="mt-5 gap-2" onClick={submit}>
          <HugeiconsIcon icon={Clock01Icon} size={18} /> Enviar a revisión
        </Button>
      )}
    </div>
  );
}

function ReviewQueue() {
  const [data, setData] = useState({ items: [], can_review: false });
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [createType, setCreateType] = useState(null);
  const load = useCallback(
    () =>
      api
        .get('/toolkit/review-queue')
        .then(({ data: result }) => setData(result))
        .catch((e) => toast.error(apiError(e))),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const review = async (decision) => {
    try {
      await api.post(`/toolkit/assets/${selected.id}/${decision}`, { notes });
      toast.success(decision === 'approve' ? 'Recurso publicado' : 'Recurso devuelto al autor');
      setSelected(null);
      setNotes('');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <>
      <div className="mb-8 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2 text-primary">
              <HugeiconsIcon icon={Clock01Icon} size={24} />
            </div>
            <h2 className="font-display text-3xl font-bold">Cola de Revisión</h2>
          </div>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Espacio transparente donde la comunidad comparte recursos y los responsables de gobernanza aseguran su
            calidad antes de publicarlos.
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="gap-2">
              <HugeiconsIcon icon={Add01Icon} size={18} />
              Agregar recurso
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>Selecciona el tipo de recurso</DropdownMenuLabel>
            <DropdownMenuGroup>
              {Object.entries(RESOURCE_TYPES).map(([key, resource]) => (
                <DropdownMenuItem key={key} onSelect={() => setCreateType(key)}>
                  <HugeiconsIcon icon={resource.icon} />
                  <span className="flex flex-col">
                    <span>{resource.label}</span>
                    <span className="text-xs text-muted-foreground">Compartir {resource.singular}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {!data.can_review && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Puedes consultar lo que está en revisión. La decisión final corresponde a administradores y evaluadores de
          VIA.
        </div>
      )}
      {data.items.length ? (
        <div className="space-y-4">
          {data.items.map((asset) => (
            <Card key={asset.id} className="border-border/80 shadow-none transition-colors hover:border-primary/30">
              <CardContent className="flex flex-col gap-5 p-6 md:flex-row md:items-center">
                <div className="flex-1">
                  <div className="mb-2 flex flex-wrap gap-2">
                    <Badge variant="outline">{TYPE_BY_DB_NAME[asset.type]?.label || asset.type}</Badge>
                    <Badge variant="secondary">{asset.domain}</Badge>
                  </div>
                  <h3 className="font-display text-xl font-bold">{asset.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{asset.description}</p>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Enviado por {asset.owner_name} · {new Date(asset.submitted_at).toLocaleDateString('es-CL')}
                  </p>
                </div>
                <Button variant={data.can_review ? 'default' : 'outline'} onClick={() => setSelected(asset)}>
                  {data.can_review ? 'Revisar recurso' : 'Ver recurso'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState title="La cola está al día" body="No hay recursos pendientes de revisión." />
      )}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{selected?.title}</DialogTitle>
            <DialogDescription>{selected?.description}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="rounded-xl border bg-muted/30 p-5">
              <article className="prose-compact font-mono text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{selected.content}</ReactMarkdown>
              </article>
            </div>
          )}
          {data.can_review && (
            <div className="space-y-2">
              <Label htmlFor="review-notes">Observaciones para el autor</Label>
              <Textarea
                id="review-notes"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Explica ajustes necesarios o deja constancia de la aprobación…"
              />
            </div>
          )}
          <DialogFooter>
            {data.can_review ? (
              <>
                <Button
                  variant="outline"
                  className="gap-2 text-destructive"
                  onClick={() => review('reject')}
                  disabled={!notes.trim()}>
                  <HugeiconsIcon icon={CancelCircleIcon} size={18} />
                  Solicitar ajustes
                </Button>
                <Button className="gap-2" onClick={() => review('approve')}>
                  <HugeiconsIcon icon={CheckmarkCircle02Icon} size={18} />
                  Aprobar y publicar
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setSelected(null)}>
                Cerrar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {createType && (
        <AssetEditor
          open
          type={createType}
          submitOnSave
          onOpenChange={(open) => !open && setCreateType(null)}
          onSaved={load}
        />
      )}
    </>
  );
}

function AssetEditor({ open, onOpenChange, type, onSaved, submitOnSave = false }) {
  const meta = RESOURCE_TYPES[type];
  const [form, setForm] = useState({ title: '', description: '', content: '', domain: '', tags: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ title: '', description: '', content: '', domain: '', tags: '' });
  }, [open]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/toolkit/${type}`, {
        ...form,
        tags: form.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      });
      if (submitOnSave) {
        await api.post(`/toolkit/assets/${data.id}/submit`);
        toast.success('Recurso agregado a la Cola de Revisión');
        onOpenChange(false);
        onSaved();
      } else {
        toast.success('Borrador guardado. Revísalo antes de enviarlo.');
        onOpenChange(false);
        onSaved();
        window.location.assign(`/toolkit/${type}/${data.id}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Compartir {meta.singular}</DialogTitle>
          <DialogDescription>
            {submitOnSave
              ? 'Al guardar, el recurso quedará inmediatamente disponible en la Cola de Revisión.'
              : 'Se guardará como borrador. Podrás revisarlo y enviarlo a la Cola de Revisión.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 py-2">
          <Field label="Título" required>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Un nombre claro y orientado a la tarea"
            />
          </Field>
          <Field label="Descripción" required>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Qué resuelve y para quién"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Dominio" required>
              <Input
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                placeholder="Ej. Operaciones Clínicas"
              />
            </Field>
            <Field label="Etiquetas">
              <Input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="seguridad, calidad, copilot"
              />
            </Field>
          </div>
          <Field label="Contenido en Markdown" required>
            <Textarea
              rows={14}
              className="font-mono text-sm"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="# Instrucciones\n\nEscribe aquí el recurso reutilizable…"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving || !form.title.trim() || !form.content.trim() || !form.domain.trim()}>
            {saving ? 'Guardando…' : submitOnSave ? 'Agregar a revisión' : 'Guardar borrador'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}
function EmptyState({ title, body, action }) {
  return (
    <div className="rounded-2xl border border-dashed bg-muted/20 px-6 py-16 text-center">
      <div className="mx-auto mb-4 w-fit rounded-full bg-primary/10 p-3 text-primary">
        <HugeiconsIcon icon={BookOpen01Icon} size={26} />
      </div>
      <h3 className="font-display text-xl font-bold">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{body}</p>
      {action && (
        <Button className="mt-5" onClick={action}>
          Compartir recurso
        </Button>
      )}
    </div>
  );
}
